<script setup lang="ts">
    import { useMediaQuery } from '@vueuse/core';
    import { computed, useId } from 'vue';
    import { useI18n } from 'vue-i18n';

    // The configurator's loader: a frosted GOBELETCUP cup that fills up in the brand's teal. With a
    // `progress` between 0 and 100 the level follows it; without one (or once it is done) the cup
    // fills and empties on its own, so a wait of unknown length still moves. Drawn in the logo's two
    // greens, the white of a frosted cup and the small vertical relief the real ones carry near the
    // base. The animation is SVG's own (SMIL), so it needs no keyframes in the stylesheet.
    // A wait that asks for no motion gets a cup half full, still.

    type TSize = 'lg' | 'md' | 'sm';

    const { progress = undefined, size = 'md' } = defineProps<{ progress?: number; size?: TSize }>();

    // The liquid's travel inside the cup, in the drawing's units: from the base to just under the rim.
    const FILL_TRAVEL = 90;
    const REDUCED_MOTION_LEVEL = 0.5;
    const CUP_PATH = 'M12 14H68L60 100Q59.4 106 53 106H27Q20.6 106 20 100Z';
    // A wave twice as wide as the cup, slid sideways to make the surface move.
    const WAVE_PATH = 'M-80 0Q-60 -4 -40 0T0 0T40 0T80 0T120 0T160 0V120H-80Z';

    const { t } = useI18n();
    const clipId = useId();
    const gradientId = useId();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    const isDeterminate = computed(() => progress !== undefined && progress > 0 && progress < 100);
    const level = computed(() => (prefersReducedMotion.value ? REDUCED_MOTION_LEVEL : Math.min(Math.max((progress ?? 0) / 100, 0), 1)));
    const isStatic = computed(() => isDeterminate.value || prefersReducedMotion.value);
    const offsetInUnits = computed(() => Math.round((1 - level.value) * FILL_TRAVEL * 10) / 10);

    const sizeClass = computed(() => {
        switch (size) {
            case 'lg':
                return 'h-28';
            case 'sm':
                return 'h-10';
            default:
                return 'h-20';
        }
    });
</script>

<template>
    <svg
        :class="[sizeClass, 'w-auto shrink-0']"
        viewBox="0 0 80 120"
        role="img"
        :aria-label="t('loader_label')"
        :data-level="isStatic ? level : undefined"
        data-test="cup-loader"
    >
        <defs>
            <clipPath :id="clipId">
                <path :d="CUP_PATH" />
            </clipPath>
            <linearGradient
                :id="gradientId"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
            >
                <stop
                    offset="0"
                    stop-color="#2CAFA5"
                />
                <stop
                    offset="1"
                    stop-color="#25777B"
                />
            </linearGradient>
        </defs>

        <!-- The glass: frosted white, the dark green's outline -->
        <path
            :d="CUP_PATH"
            fill="#FFFFFF"
            fill-opacity="0.7"
        />

        <g :clip-path="`url(#${clipId})`">
            <!-- The liquid: a surface that waves, and a body that rises -->
            <g
                v-if="isStatic"
                :style="{ transform: `translateY(${offsetInUnits}px)`, transition: 'transform 300ms ease-out' }"
                data-test="cup-loader-level"
            >
                <g transform="translate(0 16)">
                    <path
                        :d="WAVE_PATH"
                        :fill="`url(#${gradientId})`"
                    >
                        <animateTransform
                            v-if="!prefersReducedMotion"
                            attributeName="transform"
                            type="translate"
                            from="0 0"
                            to="-80 0"
                            dur="1.4s"
                            repeatCount="indefinite"
                        />
                    </path>
                </g>
            </g>
            <g
                v-else
                data-test="cup-loader-level"
            >
                <animateTransform
                    attributeName="transform"
                    type="translate"
                    values="0 90;0 0;0 0;0 90"
                    keyTimes="0;0.75;0.9;1"
                    calcMode="spline"
                    keySplines="0.45 0 0.55 1;0 0 1 1;0.4 0 1 1"
                    dur="3.2s"
                    repeatCount="indefinite"
                />
                <g transform="translate(0 16)">
                    <path
                        :d="WAVE_PATH"
                        :fill="`url(#${gradientId})`"
                    >
                        <animateTransform
                            attributeName="transform"
                            type="translate"
                            from="0 0"
                            to="-80 0"
                            dur="1.4s"
                            repeatCount="indefinite"
                        />
                    </path>
                </g>
            </g>

            <!-- The glass over the liquid: the light on its left, the relief near the base -->
            <path
                d="M17 20L23.5 98"
                fill="none"
                stroke="#FFFFFF"
                stroke-linecap="round"
                stroke-opacity="0.55"
                stroke-width="3"
            />
            <path
                d="M40 84V100"
                fill="none"
                stroke="#FFFFFF"
                stroke-linecap="round"
                stroke-opacity="0.8"
                stroke-width="1.6"
            />
        </g>

        <path
            :d="CUP_PATH"
            fill="none"
            stroke="#25777B"
            stroke-linejoin="round"
            stroke-width="2.6"
        />
        <!-- The rim: the frosted lip, seen from just above -->
        <ellipse
            cx="40"
            cy="14"
            rx="28"
            ry="4"
            fill="#FFFFFF"
            fill-opacity="0.85"
            stroke="#25777B"
            stroke-width="2.2"
        />
        <!-- The thick base -->
        <path
            d="M20.4 99H59.6"
            fill="none"
            stroke="#25777B"
            stroke-opacity="0.35"
            stroke-width="1.4"
        />
    </svg>
</template>
